import React, { useEffect, useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { fetchAllPaymentsForExport } from '../../services/payment.service';
import {
    buildExportFilename,
    buildPaymentExportRows,
    downloadTextFile,
    paymentsToCsv,
    paymentsToJson,
} from '../../utils/paymentExport';
import { useToast } from '../ui/use-toast';
import { cn } from '../../utils';
import { ALL_ORGANISATIONS_NAME } from '../../utils/organisation';

const FORMATS = [
    {
        id: 'csv',
        label: 'CSV',
        description: 'Opens in Excel and spreadsheet tools',
    },
    {
        id: 'json',
        label: 'JSON',
        description: 'Machine-readable data',
    },
];

const GROUPINGS = [
    {
        id: 'lines',
        label: 'Individual payments',
        description: 'One row per payment line, including every payee in a bulk payment',
    },
    {
        id: 'batches',
        label: 'Payment batches',
        description: 'One row per request, matching the portal list',
    },
];

const STATUS_OPTIONS = [
    { value: 'Pending', label: 'Pending' },
    { value: 'Approved', label: 'Approved' },
    { value: 'Rejected', label: 'Rejected' },
];

const defaultForm = () => ({
    format: 'csv',
    grouping: 'lines',
    statuses: STATUS_OPTIONS.map((status) => status.value),
    organisationIds: [],
    fromDate: '',
    toDate: '',
});

export default function ExportPaymentsDialog({
    children,
    token,
    organisationId,
    organisations = [],
    viewAllOrgs = false,
    timezone = null,
    organisationName = '',
}) {
    const { toast } = useToast();
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(defaultForm);
    const [exporting, setExporting] = useState(false);
    const [progress, setProgress] = useState(null);

    useEffect(() => {
        if (open) {
            setForm(defaultForm());
            setExporting(false);
            setProgress(null);
        }
    }, [open]);

    const updateForm = (patch) => setForm((prev) => ({ ...prev, ...patch }));

    const toggleStatus = (value) => {
        setForm((prev) => {
            const selected = prev.statuses.includes(value)
                ? prev.statuses.filter((status) => status !== value)
                : [...prev.statuses, value];
            return { ...prev, statuses: selected };
        });
    };

    const toggleOrganisation = (orgId) => {
        setForm((prev) => {
            const selected = prev.organisationIds.includes(orgId)
                ? prev.organisationIds.filter((id) => id !== orgId)
                : [...prev.organisationIds, orgId];
            return { ...prev, organisationIds: selected };
        });
    };

    const handleExport = async () => {
        if (exporting) return;

        const organisationsToFetch = viewAllOrgs
            ? (form.organisationIds.length > 0
                ? organisations.filter((org) => form.organisationIds.includes(String(org.id)))
                : organisations)
            : [];

        if (viewAllOrgs && organisationsToFetch.length === 0) {
            toast({
                title: 'No organisations available',
                description: 'There are no organisations to export payments from.',
                variant: 'destructive',
            });
            return;
        }

        if (form.statuses.length === 0) {
            toast({
                title: 'Select a status',
                description: 'Choose at least one status to export.',
                variant: 'destructive',
            });
            return;
        }

        const paymentType = form.statuses.join(',');
        const filtering = {
            ...(form.fromDate && { fromDate: form.fromDate }),
            ...(form.toDate && { toDate: form.toDate }),
        };

        setExporting(true);
        setProgress({ page: 1, lastPage: 1, count: 0 });

        try {
            const { payments, truncated } = await fetchAllPaymentsForExport({
                token,
                organisationId,
                organisations: organisationsToFetch,
                viewAllOrgs,
                paymentType,
                filtering,
                onProgress: setProgress,
            });

            if (payments.length === 0) {
                toast({
                    title: 'No payments to export',
                    description: 'No payments match the selected filters.',
                });
                return;
            }

            const rows = buildPaymentExportRows(payments, {
                timezone,
                includeOrganisation: viewAllOrgs,
                grouping: form.grouping,
            });

            const exportOrganisationName = !viewAllOrgs
                ? (organisationName
                    || organisations.find((org) => String(org.id) === String(organisationId))?.name
                    || 'organisation')
                : (organisationsToFetch.length === 1
                    ? organisationsToFetch[0].name
                    : ALL_ORGANISATIONS_NAME);

            const filename = buildExportFilename(form.format, {
                organisationName: exportOrganisationName,
                timezone,
            });
            if (form.format === 'json') {
                downloadTextFile(paymentsToJson(rows), filename, 'application/json');
            } else {
                downloadTextFile(
                    paymentsToCsv(rows, {
                        includeOrganisation: viewAllOrgs,
                        grouping: form.grouping,
                    }),
                    filename,
                    'text/csv;charset=utf-8'
                );
            }

            const rowLabel = form.grouping === 'batches'
                ? (rows.length === 1 ? 'payment' : 'payments')
                : (rows.length === 1 ? 'payment line' : 'payment lines');
            toast({
                title: 'Export complete',
                description: truncated
                    ? `Exported the first ${rows.length} ${rowLabel}. The result was truncated at the maximum page limit.`
                    : `Exported ${rows.length} ${rowLabel}.`,
            });
            setOpen(false);
        } catch (error) {
            toast({
                title: 'Export failed',
                description: error.message || 'An unexpected error occurred while exporting payments.',
                variant: 'destructive',
            });
        } finally {
            setExporting(false);
            setProgress(null);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(nextOpen) => { if (!exporting) setOpen(nextOpen); }}>
            <DialogTrigger asChild>
                {children || (
                    <Button variant="ghost" size="sm" className="gap-1.5 text-slate-500 font-medium hover:text-slate-700">
                        <Download size={14} />
                        Export
                    </Button>
                )}
            </DialogTrigger>
            <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Export payments</DialogTitle>
                    <DialogDescription>
                        Choose a file format and optional filters. Every matching payment is included, not just the current page.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex flex-col gap-5 py-2">
                    <div className="flex flex-col gap-2">
                        <Label>Format</Label>
                        <div className="grid grid-cols-2 gap-2">
                            {FORMATS.map((option) => {
                                const selected = form.format === option.id;
                                return (
                                    <button
                                        key={option.id}
                                        type="button"
                                        disabled={exporting}
                                        onClick={() => updateForm({ format: option.id })}
                                        className={cn(
                                            'rounded-md border px-3 py-3 text-left transition-colors',
                                            selected
                                                ? 'border-yavrio-blue bg-yavrio-blue/5'
                                                : 'border-slate-200 bg-white hover:bg-slate-50'
                                        )}
                                    >
                                        <span className="block text-sm font-semibold">{option.label}</span>
                                        <span className="block text-xs text-slate-500 mt-0.5">{option.description}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label>Rows</Label>
                        <div className="grid grid-cols-2 gap-2">
                            {GROUPINGS.map((option) => {
                                const selected = form.grouping === option.id;
                                return (
                                    <button
                                        key={option.id}
                                        type="button"
                                        disabled={exporting}
                                        onClick={() => updateForm({ grouping: option.id })}
                                        className={cn(
                                            'rounded-md border px-3 py-3 text-left transition-colors',
                                            selected
                                                ? 'border-yavrio-blue bg-yavrio-blue/5'
                                                : 'border-slate-200 bg-white hover:bg-slate-50'
                                        )}
                                    >
                                        <span className="block text-sm font-semibold">{option.label}</span>
                                        <span className="block text-xs text-slate-500 mt-0.5">{option.description}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label>Status</Label>
                        <div className="grid grid-cols-3 gap-2">
                            {STATUS_OPTIONS.map((status) => {
                                const checkboxId = `export-status-${status.value}`;
                                return (
                                    <label
                                        key={status.value}
                                        htmlFor={checkboxId}
                                        className="flex items-center gap-2 text-sm"
                                    >
                                        <Checkbox
                                            id={checkboxId}
                                            checked={form.statuses.includes(status.value)}
                                            disabled={exporting}
                                            onCheckedChange={() => toggleStatus(status.value)}
                                        />
                                        {status.label}
                                    </label>
                                );
                            })}
                        </div>
                    </div>

                    {viewAllOrgs && organisations.length > 0 && (
                        <div className="flex flex-col gap-2">
                            <Label>Organisation</Label>
                            <p className="text-xs text-slate-500">Leave all unchecked to include every organisation.</p>
                            <div className="flex flex-col gap-2 max-h-36 overflow-y-auto rounded-md border border-slate-200 p-3">
                                {organisations.map((organisation) => {
                                    const orgId = String(organisation.id);
                                    const checkboxId = `export-org-${orgId}`;
                                    const checked = form.organisationIds.includes(orgId);
                                    return (
                                        <label
                                            key={orgId}
                                            htmlFor={checkboxId}
                                            className="flex items-center gap-2 text-sm"
                                        >
                                            <Checkbox
                                                id={checkboxId}
                                                checked={checked}
                                                disabled={exporting}
                                                onCheckedChange={() => toggleOrganisation(orgId)}
                                            />
                                            {organisation.name}
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-3">
                        <div className="flex flex-col gap-2">
                            <Label htmlFor="export-from-date">From</Label>
                            <Input
                                id="export-from-date"
                                type="date"
                                value={form.fromDate}
                                max={form.toDate || undefined}
                                disabled={exporting}
                                onChange={(e) => updateForm({ fromDate: e.target.value })}
                            />
                        </div>
                        <div className="flex flex-col gap-2">
                            <Label htmlFor="export-to-date">To</Label>
                            <Input
                                id="export-to-date"
                                type="date"
                                value={form.toDate}
                                min={form.fromDate || undefined}
                                disabled={exporting}
                                onChange={(e) => updateForm({ toDate: e.target.value })}
                            />
                        </div>
                    </div>

                    {exporting && (
                        <p className="text-sm text-slate-500">
                            Fetching payments
                            {progress?.lastPage
                                ? ` (page ${progress.page} of ${progress.lastPage})`
                                : '…'}
                        </p>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" disabled={exporting} onClick={() => setOpen(false)}>
                        Cancel
                    </Button>
                    <Button className="bg-yavrio-green gap-2" disabled={exporting} onClick={handleExport}>
                        {exporting ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                        {exporting ? 'Exporting…' : 'Export'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
